import React, { useState } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import Modal from './Modal'

interface CreateClubProps {
  onClose: () => void
  onSuccess: (club: { id: number; name: string; created_at: string }) => void
}

export default function CreateClub({ onClose, onSuccess }: CreateClubProps) {
  const [name, setName] = useState('')
  const [errors, setErrors] = useState<string[]>([])
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors([])
    setLoading(true)

    try {
      const response = await fetchWithCsrf('/api/v1/clubs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          club: { name },
        }),
      })

      const data = await response.json()

      if (response.ok) {
        onSuccess(data.club)
        onClose()
      } else {
        setErrors(data.errors || ['Something went wrong'])
      }
    } catch (err) {
      setErrors(['Something went wrong. Please try again.'])
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      size="md"
      showCloseButton={true}
    >
      <div className="p-8">
        <div className="mb-8">
          <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl mb-4">
            <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Start your club</h2>
          <p className="text-gray-600">Give your book club a name and you'll be all set</p>
        </div>

        {errors.length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <ul className="text-red-600 text-sm space-y-1">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="club-name" className="block text-sm font-medium text-gray-700 mb-2">
              Club Name
            </label>
            <input
              id="club-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
              placeholder="e.g. The Page Turners"
              autoFocus
            />
            <p className="mt-2 text-sm text-gray-500">
              Choose a name that represents your club's vibe
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30"
          >
            {loading ? 'Creating club...' : 'Create club'}
          </button>
        </form>

        <div className="mt-6 p-4 bg-indigo-50 rounded-lg">
          <p className="text-sm text-indigo-700">
            <strong>You'll be the first member!</strong> After creating your club, you can invite others to join.
          </p>
        </div>
      </div>
    </Modal>
  )
}
